/**
 * simInteractionStore — 시뮬레이터 맵 상호작용용 경량 zustand 스토어.
 *
 * 맵 위 비상벨 마커를 클릭하면 클릭 레이어가 requestBellTrigger 로
 * 트리거 요청을 게시하고, EmergencyBellSimulator 가 이를 구독해 실제
 * triggerEmergency 를 호출한다. nonce 는 같은 벨을 연속 클릭해도 매번
 * 새 요청으로 인식되도록 단조 증가시킨다.
 *
 * statusStore 와 동일한 create<T>((set, get) => ({...})) 스타일.
 */
import { create } from 'zustand';

export type BellTriggerAction = 'alert' | 'release';

export interface PendingBellTrigger {
  bellId: string;
  action: BellTriggerAction;
  /** 같은 벨 연속 클릭을 구분하기 위한 단조 증가 카운터. */
  nonce: number;
}

export interface SelectedCctv {
  name: string;
  /** 같은 CCTV 연속 클릭도 새 선택으로 인식되도록 단조 증가. */
  nonce: number;
}

/**
 * 맵 포커스 — 탭(만공차/조명/비상벨)에서 CCTV·비상벨을 선택하거나 맵에서
 * 객체를 클릭했을 때, 맵이 해당 객체를 하이라이트(선택 테두리 + 펄스 링)하고
 * 그 위치로 센터링하도록 게시하는 단일 채널.
 * 비상벨은 cell id 로, CCTV 는 이름으로 식별한다(층별 셀 해석은 MapCanvasPanel).
 */
export interface MapFocus {
  kind: 'bell' | 'cctv';
  /** 비상벨 cell id (kind==='bell'). */
  id?: string;
  /** CCTV 이름 (kind==='cctv'). */
  name?: string;
  /** 같은 객체 재선택도 센터링이 다시 일어나도록 단조 증가. */
  nonce: number;
}

interface SimInteractionStore {
  pendingBellTrigger: PendingBellTrigger | null;
  /** 맵에서 마지막으로 클릭된 CCTV (비상벨 탭의 수동 CCTV 입력 채우기용). */
  selectedCctv: SelectedCctv | null;
  /**
   * 점유된 주차면 cell id 목록(만공차 체크 탭 ↔ 맵 공유 단일 소스).
   * 맵에서 주차면 클릭/탭에서 토글 → 여기에 반영 → 맵은 빨간색, 탭은 만공차 계산.
   */
  occupiedParkingIds: string[];
  /** 맵이 하이라이트·센터링할 현재 포커스 객체 (탭 선택/맵 클릭 공유). */
  mapFocus: MapFocus | null;
  /** 맵 클릭 등으로 비상벨 트리거를 요청한다 (nonce 증가). */
  requestBellTrigger: (bellId: string, action: BellTriggerAction) => void;
  /** 맵에서 CCTV 를 클릭했을 때 그 이름을 게시한다 (nonce 증가). */
  selectCctv: (name: string) => void;
  /** 맵 포커스 설정 (nonce 증가). ref 없이 호출하면 포커스 해제. */
  setMapFocus: (focus: Omit<MapFocus, 'nonce'> | null) => void;
  /** 주차면 점유 토글(맵 클릭/칩 클릭). */
  toggleParkingOccupied: (id: string) => void;
  /** 여러 주차면 점유를 한 번에 설정(전체 점유/해제). */
  setParkingOccupied: (ids: string[], occupied: boolean) => void;
  /** 구독 측에서 요청을 소비한 뒤 비운다. */
  consumePending: () => void;
}

export const useSimInteractionStore = create<SimInteractionStore>((set, get) => ({
  pendingBellTrigger: null,
  selectedCctv: null,
  occupiedParkingIds: [],
  mapFocus: null,

  setMapFocus: (focus) => {
    if (!focus) {
      set({ mapFocus: null });
      return;
    }
    const prevNonce = get().mapFocus?.nonce ?? 0;
    set({ mapFocus: { ...focus, nonce: prevNonce + 1 } });
  },

  toggleParkingOccupied: (id: string) => {
    const cur = get().occupiedParkingIds;
    set({
      occupiedParkingIds: cur.includes(id)
        ? cur.filter(x => x !== id)
        : [...cur, id],
    });
  },

  setParkingOccupied: (ids: string[], occupied: boolean) => {
    const cur = new Set(get().occupiedParkingIds);
    if (occupied) ids.forEach(i => cur.add(i));
    else ids.forEach(i => cur.delete(i));
    set({ occupiedParkingIds: Array.from(cur) });
  },

  requestBellTrigger: (bellId: string, action: BellTriggerAction) => {
    const prevNonce = get().pendingBellTrigger?.nonce ?? 0;
    set({
      pendingBellTrigger: { bellId, action, nonce: prevNonce + 1 },
    });
  },

  selectCctv: (name: string) => {
    const prevNonce = get().selectedCctv?.nonce ?? 0;
    set({ selectedCctv: { name, nonce: prevNonce + 1 } });
  },

  consumePending: () => {
    set({ pendingBellTrigger: null });
  },
}));
